<template>
  <div class="hero-section">
    <div class="container">
      <div class="row justify-center relative-position hero-section-container">
        <div class="col-12 col-md-5 text-white q-gutter-md hero-text-col">
          <div class="column q-pa-md">
            <h2 class="title">
              {{ $t('home.hero_section.title') }} <br />
              @SiG
            </h2>
            <h1 class="subtitle">{{ $t('home.hero_section.subtitle') }}</h1>

            <p style="max-width: 350px">
              {{ $t('home.hero_section.paragraph') }}
            </p>

            <div class="row q-gutter-md items-center">
              <q-btn
                :label="$t('home.hero_section.btn1_label')"
                :to="{ name: 'Programmes' }"
                color="secondary"
                text-color="dark"
                no-caps
                rounded
              />
              <q-btn
                :label="$t('home.hero_section.btn2_label')"
                :to="{ name: 'Universities' }"
                color="secondary"
                text-color="dark"
                no-caps
                rounded
              />
              <q-btn
                :label="$t('program_matcher.hero_btn')"
                icon="auto_awesome"
                color="white"
                no-caps
                rounded
                outline
                @click="matcherOpen = true"
                class="hero-matcher-btn"
              />
            </div>
          </div>
        </div>

        <div v-if="$q.screen.gt.sm" class="col-md-auto">
          <div class="yellow-circle"></div>
        </div>

        <div class="col-12 col-md-7 hero-image-wrapper">
          <q-img
            src="~assets/home/hero-image.jpg"
            class="hero-image"
            img-alt="AtSiG Home"
            :ratio="$q.screen.lt.md ? undefined : 3 / 4"
          />
        </div>
      </div>
      <q-no-ssr>
        <div class="row justify-center full-width">
          <q-form
            @submit="searchProgrammes"
            class="search-bar yellow-bottom-card col-11 col-sm-10 col-md-8 col-xl-6 row items-center"
          >
            <q-select
              class="col-6 col-sm"
              v-model="query_prog_type"
              :options="typeOptions"
              map-options
              emit-value
              :label="$t('home.search_prog_type')"
            >
              <template v-if="query_prog_type != undefined" v-slot:append>
                <q-icon
                  name="cancel"
                  @click.stop.prevent="query_prog_type = undefined"
                  class="cursor-pointer"
                />
              </template>
            </q-select>

            <q-input class="col-6 col-sm" v-model="query_name" :label="$t('home.search_prog_name')">
              <template v-if="query_name != undefined" v-slot:append>
                <q-icon
                  name="cancel"
                  @click.stop.prevent="query_name = undefined"
                  class="cursor-pointer"
                />
              </template>
            </q-input>

            <q-select
              class="col-6 col-sm"
              v-model="query_unis"
              :options="filteredUniversities"
              :label="$t('home.search_uni')"
              map-options
              emit-value
              use-input
              hide-selected
              fill-input
              @filter="filterUnis"
            >
              <template v-if="query_unis != undefined" v-slot:append>
                <q-icon
                  name="cancel"
                  @click.stop.prevent="query_unis = undefined"
                  class="cursor-pointer"
                />
              </template>
            </q-select>

            <q-select
              class="col-6 col-sm"
              v-model="query_cities"
              :options="filteredUniCities"
              :label="$t('home.search_city')"
              map-options
              emit-value
              use-input
              hide-selected
              fill-input
              @filter="filterCities"
            >
              <template v-if="query_cities != undefined" v-slot:append>
                <q-icon
                  name="cancel"
                  @click.stop.prevent="query_cities = undefined"
                  class="cursor-pointer"
                />
              </template>
            </q-select>

            <div class="col-12 col-sm-auto flex justify-end">
              <q-btn
                size="14px"
                color="secondary"
                text-color="dark"
                rounded
                :label="$t('home.search_btn')"
                type="submit"
                no-caps
                class="full-width"
              >
                <q-icon name="search" class="q-ml-sm" />
              </q-btn>
            </div>
          </q-form>
        </div>
      </q-no-ssr>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useProgrammeOptions } from 'src/composables/programme/useProgrammeOptions'
import { useUniversityStore } from 'stores/university-store'
import { useUniCityStore } from 'stores/uni_city-store'
import { useMatcherState } from 'src/composables/useMatcherState'

const { locale } = useI18n()
const router = useRouter()
const { open: matcherOpen } = useMatcherState()

const { typeOptions } = useProgrammeOptions()

// Stores
const universityStore = useUniversityStore()
const uniCityStore = useUniCityStore()

const { paginatedUnis } = storeToRefs(universityStore)
const { paginatedUniCities } = storeToRefs(uniCityStore)

// Query refs
const query_prog_type = ref(undefined)
const query_name = ref(undefined)
const query_unis = ref(undefined)
const query_cities = ref(undefined)

// Search input refs
const uni_search = ref('')
const city_search = ref('')

// Dropdown options
const filteredUniversities = computed(() =>
  paginatedUnis.value.map((u) => ({
    label: locale.value === 'el-GR' ? u.name_gr : u.name_en,
    value: u.id,
  }))
)

const filteredUniCities = computed(() =>
  paginatedUniCities.value.map((c) => ({
    label: locale.value === 'el-GR' ? c.name_gr : c.name_en,
    value: locale.value === 'el-GR' ? c.name_gr : c.name_en,
  }))
)

// Filtering handlers
async function filterUnis(val: string, update: (callback: () => void) => void) {
  uni_search.value = val
  await universityStore.searchUniversities(
    { limit: 100, sort: 'name_en', order: 'asc' },
    { name: val }
  )
  update(() => {}) // Refresh select options
}

async function filterCities(val: string, update: (callback: () => void) => void) {
  city_search.value = val
  uniCityStore.filters.limit = 100
  uniCityStore.filters.q = val
  uniCityStore.filters.sort = 'name_en'
  uniCityStore.filters.order = 'asc'
  await uniCityStore.searchUniCities()
  update(() => {}) // Refresh select options
}

// Search button action
async function searchProgrammes() {
  await router.push({
    name: 'Programmes',
    query: {
      name: query_name.value,
      prog_type: query_prog_type.value,
      city: query_cities.value,
      university_id: Number(query_unis.value) || undefined,
    },
  })
}
</script>
<style lang="scss" scoped>
.hero-matcher-btn {
  opacity: 0.85;
  padding: 8px 20px !important;
  transition: opacity 0.15s;

  &:hover { opacity: 1; }
}

.yellow-circle {
  position: absolute;
  top: 13%;
  left: 35%;
  width: 150px;
  height: 150px;
  background: $accent;
  border-radius: 50%;
  z-index: 1;
}

.yellow-circle::after {
  content: '';
  position: absolute;
  top: 20px;
  right: -40px;
  width: 80px;
  height: 80px;
  background-image: radial-gradient($primary 2px, transparent 2px);
  background-size: 12px 12px;
  //opacity: 0.8;
  color: $primary;
}
</style>
